import { memo, useState, useRef, useCallback } from 'react';

import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';

import DatePickerMenu from './date-picker-menu';
import DatePickerInput, { ClassNames } from './date-picker-input';

interface DatePickerProps {
  date: Date | null;
  handleChange: (date: Date | null, name?: string) => void;
  minDate?: Date | null;
  maxDate?: Date | null;
  startFrom?: number;
  endIn?: number;
  disabled?: boolean;
  format?: string;
  name?: string;
  placeholder?: string;
  input?: boolean;
  cls?: {
    output?: ClassNames;
  };
  variant?: 'standard' | 'underline';
}

const DatePicker = ({
  date,
  handleChange,
  minDate,
  maxDate,
  name,
  cls,
  disabled = false,
  startFrom,
  endIn,
  format,
  placeholder,
  input = false,
  variant = 'standard',
}: DatePickerProps) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
    }
  };

  const handleChangeDate = useCallback(
    (date: Date | null) => {
      handleChange(date, name);
      setOpen(false);
    },
    [handleChange, name]
  );

  return (
    <>
      <DatePickerInput
        ref={anchorRef}
        date={date}
        name={name}
        placeholder={placeholder}
        handleChangeDate={handleChange}
        handleToggleCalendar={handleToggle}
        minDate={minDate}
        maxDate={maxDate}
        cls={cls?.output}
        disabled={disabled}
        variant={variant}
      />
      <Popper
        style={{ zIndex: 1300 }}
        anchorEl={anchorRef.current}
        open={open}
        role={undefined}
        transition
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <div onKeyDown={handleListKeyDown}>
                  <DatePickerMenu
                    date={date}
                    handleChange={handleChangeDate}
                    minDate={minDate}
                    maxDate={maxDate}
                    startFrom={startFrom}
                    endIn={endIn}
                  />
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default memo(DatePicker);
